import { createSelector } from "reselect"
import { get, first, last, chain, size, some, find } from "lodash"
import { getStartDate, getEndDate } from "selectors/dates"
import { getSelectedViewsFactors, getSelectedViews } from "selectors/factors"
import { getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { aggregateExposuresFactors } from "helpers/factorClassification/index"
import { getContentSets } from "selectors/contentSets"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { getDefaultMetricUnits } from "modules/default-metric-units/selectors"
import { METRIC_TYPES } from "helpers/assets/constants"

const defaultExposureData = {}
export const getExposureData = state => get(state, "global.data.exposure.data.model.simulation.exposure", defaultExposureData)
const getRawExposureUnit = state => get(state, "modules.analyzeHistoricalExposure.unit")
const getSelectedContentSetId = (_, props) => get(props, "contentSetId") || get(props, "params.contentSetId", null)

export const getIsSensitivityContentSet = createSelector(
  [getContentSets, getSelectedContentSetId],
  (contentSets, selectedContentSetId) => {
    const contentSet = find(contentSets, { id: selectedContentSetId })
    return contentSet ? contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY : false
  },
)

export const getExposureUnit = createSelector(
  [getDefaultMetricUnits, getRawExposureUnit, getIsSensitivityContentSet, getSelectedContentSetId],
  (units, exposureUnit, isSensitivityContentSet, selectedContentSetId) => {
    if (selectedContentSetId) {
      return isSensitivityContentSet ? METRIC_TYPES.CURRENCY_EXPOSURE : METRIC_TYPES.NET_CONTRIBUTION
    }

    return exposureUnit || get(units, "exposure", METRIC_TYPES.NET_CONTRIBUTION)
  },
)

function formatExposure(exposure, mustAggregateFactors, styleAggregation, selectedViewFactors, selectedViews, factorsClassification, exposureUnit) {
  const count = size(exposure)
  const exposureField = exposureUnit === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"

  return chain(exposure)
    .map(({ date, factors }, index) => {
      // Always return last date
      if (factors.length || index === count - 1) {
        let exposureFactors = factors
        if (mustAggregateFactors) {
          exposureFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, exposureField)
        }

        if (size(selectedViews)) {
          exposureFactors = chain(factors)
            .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
            .map(factor => ({ ...factor, customView: true }))
            .concat(exposureFactors)
            .value()
        }

        return ({
          date,
          factors: chain(exposureFactors)
            .keyBy("id")
            .mapValues(exposureField)
            .value(),
        })
      }

      return null
    })
    .filter(data => data)
    .value()
}

export const getExposure = createSelector(
  [getExposureData, getMustAggregateFactors, getStyleExposureAggregation, getSelectedViewsFactors, getSelectedViews, getModelFactorsClassification, getExposureUnit],
  formatExposure,
)

export const getFilteredExposure = createSelector(
  [getExposure, getStartDate, getEndDate],
  (exposure, startDate, endDate) =>
    exposure.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getExposure],
  (exposure) => first(exposure),
)

export const getCurrent = createSelector(
  [getExposure],
  (exposure) => last(exposure),
)

export const getFirstHistoricalExposureDate = createSelector(
  [getFilteredExposure],
  (exposure) => get(first(exposure), "date", null),
)
